import React, { useCallback } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Grid,
  GridItem,
  Label,
  Tooltip,
} from '@patternfly/react-core';
import { ShieldAltIcon } from '@patternfly/react-icons';
import { Server } from '../../../types/models/Server';
import { useApiFetch } from '../../../api';
import { useRoles } from '../../../contexts/roles/RolesContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import InlineEditSelect from '../../../components/InlineEditSelect/InlineEditSelect';
import renderStatusLabel from './serverStatusLabel';

interface ServerOverviewTabProps {
  server: Server;
  onServerUpdate: () => void;
}

const ServerOverviewTab: React.FunctionComponent<ServerOverviewTabProps> = ({ server, onServerUpdate }) => {
  const apiFetch = useApiFetch();
  const { roles } = useRoles();
  const { addNotification, setDrawerOpen } = useNotifications();

  const roleOptions = roles.filter((r) => r.id !== null).map((r) => ({ value: r.id as number, label: r.name }));

  const handleRoleSave = useCallback(
    async (newValue: string | number | null) => {
      try {
        const response = await apiFetch(`/datacenters/servers/${server.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ role: newValue }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status !== 'success') {
          throw new Error('Unexpected response from server');
        }

        addNotification({
          title: 'Device role updated',
          srTitle: 'Success alert',
          description: `Device role for ${server.name} has been updated.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onServerUpdate();
      } catch (err) {
        addNotification({
          title: 'Failed to update device role',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        throw err;
      }
    },
    [apiFetch, server.id, server.name, addNotification, setDrawerOpen, onServerUpdate],
  );

  return (
    <Grid hasGutter md={6} style={{ marginTop: '1rem' }}>
      <GridItem>
        <Card isFullHeight>
          <CardTitle>General Information</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Name</DescriptionListTerm>
                <DescriptionListDescription>{server.name}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Status</DescriptionListTerm>
                <DescriptionListDescription>{renderStatusLabel(server.status)}</DescriptionListDescription>
              </DescriptionListGroup>
              {(server.is_drp_target ?? false) && (
                <DescriptionListGroup>
                  <DescriptionListTerm>DRP target hypervisor</DescriptionListTerm>
                  <DescriptionListDescription>
                    <Tooltip content="DRP backup hypervisor — VMs reported on this host become standby replicas of their primaries">
                      <Label color="blue" icon={<ShieldAltIcon />}>
                        DRP target
                      </Label>
                    </Tooltip>
                  </DescriptionListDescription>
                </DescriptionListGroup>
              )}
              <DescriptionListGroup>
                <DescriptionListTerm>Device Role</DescriptionListTerm>
                <DescriptionListDescription>
                  <InlineEditSelect
                    currentLabel={server.device_role?.name ?? null}
                    currentValue={server.device_role?.id ?? null}
                    options={roleOptions}
                    onSave={handleRoleSave}
                    placeholder="Not assigned"
                    ariaLabel="Edit server device role"
                    clearable
                    clearLabel="No role"
                  />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Device Type</DescriptionListTerm>
                <DescriptionListDescription>{server.device_type?.name ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Platform</DescriptionListTerm>
                <DescriptionListDescription>{server.platform?.name ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Serial Number</DescriptionListTerm>
                <DescriptionListDescription>{server.serial || '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Description</DescriptionListTerm>
                <DescriptionListDescription>{server.description || '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Comments</DescriptionListTerm>
                <DescriptionListDescription>{server.comments || '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem>
        <Card>
          <CardTitle>Location</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Site</DescriptionListTerm>
                <DescriptionListDescription>{server.site?.name ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Rack</DescriptionListTerm>
                <DescriptionListDescription>{server.rack?.name ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem>
        <Card>
          <CardTitle>Network</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Primary IPv4</DescriptionListTerm>
                <DescriptionListDescription>{server.primary_ip4?.address ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Primary IPv6</DescriptionListTerm>
                <DescriptionListDescription>{server.primary_ip6?.address ?? '\u2014'}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
    </Grid>
  );
};

export default ServerOverviewTab;
